/**
 * Pre-sized logo and member-photo variants, and the size a surface asks for.
 *
 * The theme bucket keeps, beside each original logo, `img/sized/<stem>/h<rung>.webp` cut to a
 * fixed ladder of pixel heights. A lobby logo drawn 240px wide used to download whatever the org
 * uploaded — a multi-megabyte PNG for some — and a member photo drawn 32px downloaded the full
 * photo. Member photos have `<hash>_sm.png` / `_md.png` / `_lg.png` beside the original.
 *
 * This is the one copy of the rung ladder in the Jitsi bundle. The static pages
 * (welcome/index.html, cancel.html, static/close*.html) cannot import it and hard-code the single
 * rung they need, with a pointer back here.
 *
 * A variant can be missing — a folder not backfilled yet, a failed generation — so every consumer
 * falls back to the ORIGINAL url exactly as it was built before: an <img> on error, a CSS
 * background or mask by probing the variant first (probeImage in useLogoVariant.web.ts — kept out
 * of this file because the native build shares it and has no Image).
 */
/** Pixel heights the server cuts every logo to. */
export const LOGO_RUNGS = [32, 48, 64, 96, 128, 192, 256, 384, 512, 768, 1024];
/** The logo files that have variants: the file's base name, exactly as the originals are named. */
const LOGO_STEMS = new Set([
    'logo', 'logo-software',
    'logo-full', 'logo-full-software',
    'logo-lifestyle', 'logo-lifestyle-software',
    'tab-icon', 'tab-icon-software'
]);
/** `<theme base>/img/<stem>.<ext>[?query]` — the only shape of original a variant is cut for. */
const THEME_LOGO = /^(.*\/img\/)([a-z-]+)\.(?:svg|png|webp|jpe?g|gif)(\?[^#]*)?$/i;
/** `…/avatars/photos/<hash>.<ext>[?query]`: an uploaded member photo. DiceBear `.svg`s never match. */
const MEMBER_PHOTO = /^(.*\/avatars\/photos\/[^/?#]+?)\.(?:png|jpe?g|webp|gif)(\?[^#]*)?$/i;
/**
 * The smallest rung at least `cssPx * dpr` tall, else the largest.
 *
 * @param {number} cssPx - The LARGEST CSS height the logo can be drawn at on the surface. When only
 * a width bound is known, that width: a wide logo drawn W wide is shorter than W and a square one is
 * exactly W, so the rung is always tall enough.
 * @param {number} dpr - Device pixel ratio. Fixed at 2 on the web, so URLs are stable.
 * @returns {number}
 */
export function pickLogoRung(cssPx, dpr = 2) {
    const need = Math.ceil(cssPx * dpr);
    return LOGO_RUNGS.find(rung => rung >= need) ?? LOGO_RUNGS[LOGO_RUNGS.length - 1];
}
/**
 * The pre-sized variant of a theme logo for a surface `cssPx` tall, or undefined when the url is
 * not a theme logo (a Jitsi default image, an empty url) — the caller then uses the original.
 *
 * A query on the original (`?v=<revision>`) is carried over.
 *
 * @param {string} originalUrl - The logo url as built before variants existed.
 * @param {number} cssPx - See {@link pickLogoRung}.
 * @returns {string|undefined}
 */
export function logoVariantUrl(originalUrl, cssPx) {
    const match = originalUrl ? THEME_LOGO.exec(originalUrl) : null;
    if (!match || !LOGO_STEMS.has(match[2])) {
        return undefined;
    }
    return `${match[1]}sized/${match[2]}/h${pickLogoRung(cssPx)}.webp${match[3] ?? ''}`;
}
/**
 * The sized variant of an uploaded member photo for an avatar `cssPx` across, or undefined when the
 * url is not one (DiceBear `.svg`, a gravatar, an already-sized url).
 *
 * The variants are always `.png`, whatever the original's type. At dpr 2: up to 25px `sm` (50px),
 * up to 75px `md` (150px), larger `lg` (350px).
 *
 * @param {string} url - The member photo url.
 * @param {number} cssPx - The avatar's drawn size; unknown means large.
 * @returns {string|undefined}
 */
export function memberPhotoSizedUrl(url, cssPx) {
    const match = url ? MEMBER_PHOTO.exec(url) : null;
    if (!match || /_(?:sm|md|lg)$/i.test(match[1])) {
        return undefined;
    }
    const px = cssPx ?? Infinity;
    const size = px <= 25 ? 'sm' : px <= 75 ? 'md' : 'lg';
    return `${match[1]}_${size}.png${match[2] ?? ''}`;
}
